import type { ImsCostingMethod } from "@redotech/crdb-enums/inventory/ims-costing-method";
import { LoadState, useLoad } from "@redotech/react-util/load";
import type { CostLedgerState } from "@redotech/redo-merchant-inventory-rpc/schema/get-cost-ledger-status";
import { useContext } from "react";
import { RedoInventoryRpcClientContext } from "../inventory-rpc-client-provider";

type CostLedgerMethod =
  | ImsCostingMethod.WeightedAverage
  | ImsCostingMethod.Fifo;

interface CostLedgerStatus {
  method: CostLedgerMethod | null;
  setup: CostLedgerState;
}

/**
 * Null when skipped, undefined until loaded. Pass `enabled: false` for users
 * without inventory access, and bump `reloadKey` to load it again.
 */
export function useCostLedgerStatus({
  enabled = true,
  checkBuild = false,
  reloadKey = 0,
}: {
  enabled?: boolean;
  /** Also reports a running build as `building`. */
  checkBuild?: boolean;
  reloadKey?: number;
} = {}): LoadState<CostLedgerStatus | null> {
  const client = useContext(RedoInventoryRpcClientContext);
  return useLoad(
    async (signal) => {
      if (!enabled || !client) {
        return null;
      }
      const { method, setup } = await client.getCostLedgerStatus(
        { checkBuild },
        { signal },
      );
      // An older server sends no setup; keep its pages hidden without a ledger.
      return { method, setup: setup ?? (method ? "on" : "needsSourceOfTruth") };
    },
    [client, enabled, checkBuild, reloadKey],
  );
}
